import * as THREE from 'three';

/**
 * Shared renderer for menu previews.
 *
 * Browsers cap the number of live WebGL contexts, and menus can show dozens of previews (armory
 * grid, wardrobe, lobby). Every preview therefore draws into its own plain 2D canvas: one lazily
 * created, offscreen THREE.WebGLRenderer renders each visible preview into the bottom-left corner
 * of its drawing buffer and the pixels are copied over with drawImage. Previews stay real DOM
 * elements, so clipping, stacking, transforms and modals keep working.
 *
 * Scheduling: only previews that intersect the viewport are ticked and drawn; when many are
 * visible, draws are budgeted per frame (stalest first). The loop stops while the document is
 * hidden, while suspended (e.g. a full-screen editor is open) or when nothing is visible, and the
 * WebGL context is released after a while with no previews attached.
 */

/** Something the shared preview renderer can draw. */
export interface PreviewScene {
  readonly scene: THREE.Scene;
  readonly camera: THREE.Camera;
  /** Advances animations by dt seconds. Returns true when the image changed and needs a redraw. */
  tick(dt: number): boolean;
  /** The preview's CSS size changed. */
  resize(width: number, height: number): void;
}

export interface PreviewHandle {
  /** Redraws on the next frame (after a model rebuild, prop change...). */
  invalidate(): void;
  /** Interactive previews (being dragged) are drawn every frame, ahead of the budget. */
  setInteracting(on: boolean): void;
  detach(): void;
}

export interface PreviewStats {
  attached: number;
  visible: number;
  draws: number;
  frames: number;
  contexts: number;
  suspended: boolean;
  /** GPU resources held by the shared renderer (three.js info.memory). */
  geometries: number;
  textures: number;
}

const MAX_DPR = 2;
/** Backing-store cap per preview (device pixels). */
const MAX_VIEW_PIXELS = 1_800_000;
/** Per-frame draw budgets once many previews are visible. */
const FRAME_VIEWS = 10;
const FRAME_PIXELS = 4_500_000;
/** Release the GL context after this long with no previews attached. */
const IDLE_RELEASE_MS = 15_000;

interface Slot {
  view: PreviewScene;
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D | null;
  visible: boolean;
  cssW: number;
  cssH: number;
  pxW: number;
  pxH: number;
  dirty: boolean;
  interacting: boolean;
  lastTick: number;
  lastDraw: number;
}

class PreviewRenderer {
  private gl: THREE.WebGLRenderer | null = null;
  private glW = 0;
  private glH = 0;
  private failed = false;
  private lost = false;
  private readonly slots = new Set<Slot>();
  private readonly byCanvas = new Map<Element, Slot>();
  private io: IntersectionObserver | null = null;
  private ro: ResizeObserver | null = null;
  private raf = 0;
  private suspends = 0;
  private releaseTimer: ReturnType<typeof setTimeout> | null = null;
  private dpr = 1;
  private contexts = 0;
  readonly stats: PreviewStats = { attached: 0, visible: 0, draws: 0, frames: 0, contexts: 0, suspended: false, geometries: 0, textures: 0 };

  attach(canvas: HTMLCanvasElement, view: PreviewScene): PreviewHandle {
    this.observers();
    const slot: Slot = {
      view,
      canvas,
      ctx: canvas.getContext('2d', { alpha: true }),
      visible: false,
      cssW: 0,
      cssH: 0,
      pxW: 0,
      pxH: 0,
      dirty: true,
      interacting: false,
      lastTick: performance.now(),
      lastDraw: 0,
    };
    this.slots.add(slot);
    this.byCanvas.set(canvas, slot);
    this.io!.observe(canvas);
    this.ro!.observe(canvas);
    this.measure(slot);
    if (this.releaseTimer) {
      clearTimeout(this.releaseTimer);
      this.releaseTimer = null;
    }
    this.stats.attached = this.slots.size;
    return {
      invalidate: () => {
        slot.dirty = true;
        this.kick();
      },
      setInteracting: (on) => {
        slot.interacting = on;
        slot.dirty = true;
        this.kick();
      },
      detach: () => this.detach(slot),
    };
  }

  /** Pauses all previews (e.g. while a full-screen view covers them). Returns the resume function. */
  suspend(): () => void {
    this.suspends++;
    this.stats.suspended = true;
    if (this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }
    let done = false;
    return () => {
      if (done) return;
      done = true;
      this.suspends--;
      this.stats.suspended = this.suspends > 0;
      for (const s of this.slots) {
        s.dirty = true;
        s.lastTick = performance.now();
      }
      this.kick();
    };
  }

  private detach(slot: Slot): void {
    if (!this.slots.delete(slot)) return;
    this.byCanvas.delete(slot.canvas);
    this.io?.unobserve(slot.canvas);
    this.ro?.unobserve(slot.canvas);
    this.stats.attached = this.slots.size;
    if (this.slots.size === 0 && this.gl && !this.releaseTimer) {
      this.releaseTimer = setTimeout(() => {
        this.releaseTimer = null;
        if (this.slots.size === 0) this.release();
      }, IDLE_RELEASE_MS);
    }
  }

  private observers(): void {
    if (this.io) return;
    this.io = new IntersectionObserver(
      (entries) => {
        const now = performance.now();
        for (const e of entries) {
          const s = this.byCanvas.get(e.target);
          if (!s) continue;
          const vis = e.isIntersecting;
          if (vis && !s.visible) {
            s.dirty = true;
            s.lastTick = now;
          }
          s.visible = vis;
        }
        this.kick();
      },
      { rootMargin: '96px' },
    );
    this.ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        const s = this.byCanvas.get(e.target);
        if (s) this.measure(s);
      }
      this.kick();
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        const now = performance.now();
        for (const s of this.slots) s.lastTick = now;
        this.kick();
      } else if (this.raf) {
        cancelAnimationFrame(this.raf);
        this.raf = 0;
      }
    });
  }

  /** Reads the canvas CSS size and sizes its backing store for the device pixel ratio. */
  private measure(s: Slot): void {
    const w = s.canvas.clientWidth;
    const h = s.canvas.clientHeight;
    this.dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
    let pw = Math.max(0, Math.round(w * this.dpr));
    let ph = Math.max(0, Math.round(h * this.dpr));
    if (pw * ph > MAX_VIEW_PIXELS) {
      const k = Math.sqrt(MAX_VIEW_PIXELS / (pw * ph));
      pw = Math.floor(pw * k);
      ph = Math.floor(ph * k);
    }
    if (w !== s.cssW || h !== s.cssH) {
      s.cssW = w;
      s.cssH = h;
      if (w > 0 && h > 0) s.view.resize(w, h);
    }
    if (pw !== s.pxW || ph !== s.pxH) {
      s.pxW = pw;
      s.pxH = ph;
      s.canvas.width = pw;
      s.canvas.height = ph;
    }
    s.dirty = true;
  }

  private kick(): void {
    if (this.raf || this.suspends > 0 || this.failed || document.visibilityState === 'hidden') return;
    let any = false;
    for (const s of this.slots) if (s.visible) any = true;
    if (any) this.raf = requestAnimationFrame(this.frame);
  }

  private renderer(): THREE.WebGLRenderer | null {
    if (this.gl || this.failed) return this.gl;
    try {
      const gl = new THREE.WebGLRenderer({ antialias: true, alpha: true, premultipliedAlpha: true, powerPreference: 'default', stencil: false });
      gl.setPixelRatio(1);
      gl.outputColorSpace = THREE.SRGBColorSpace;
      gl.setClearColor(0x000000, 0);
      const canvas = gl.domElement;
      canvas.addEventListener('webglcontextlost', (e) => {
        e.preventDefault();
        this.lost = true;
      });
      canvas.addEventListener('webglcontextrestored', () => {
        this.lost = false;
        for (const s of this.slots) s.dirty = true;
        this.kick();
      });
      this.gl = gl;
      this.glW = this.glH = 0;
      this.lost = false;
      this.contexts++;
      this.stats.contexts = this.contexts;
    } catch (err) {
      console.warn('[previews] WebGL unavailable', err);
      this.failed = true;
    }
    return this.gl;
  }

  private release(): void {
    const gl = this.gl;
    if (!gl) return;
    this.gl = null;
    this.contexts--;
    this.stats.contexts = this.contexts;
    gl.dispose();
    gl.forceContextLoss();
  }

  private readonly frame = (now: number): void => {
    this.raf = 0;
    if (this.suspends > 0) return;
    const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
    if (dpr !== this.dpr) for (const s of this.slots) this.measure(s);
    const due: Slot[] = [];
    let visible = 0;
    for (const s of this.slots) {
      if (!s.visible) continue;
      visible++;
      const dt = Math.min(0.1, Math.max(0, (now - s.lastTick) / 1000));
      s.lastTick = now;
      if (s.view.tick(dt)) s.dirty = true;
      if (s.dirty && s.pxW > 0 && s.pxH > 0 && s.ctx) due.push(s);
    }
    this.stats.visible = visible;
    if (due.length > 0) {
      const gl = this.renderer();
      if (gl && !this.lost) {
        due.sort((a, b) => Number(b.interacting) - Number(a.interacting) || a.lastDraw - b.lastDraw);
        let pixels = 0;
        let count = 0;
        const batch: Slot[] = [];
        for (const s of due) {
          const px = s.pxW * s.pxH;
          if (!s.interacting && count > 0 && (count >= FRAME_VIEWS || pixels + px > FRAME_PIXELS)) break;
          batch.push(s);
          pixels += px;
          count++;
        }
        this.fit(gl, batch);
        for (const s of batch) this.draw(gl, s, now);
        this.stats.frames++;
        this.stats.geometries = gl.info.memory.geometries;
        this.stats.textures = gl.info.memory.textures;
      }
    }
    if (visible > 0) this.raf = requestAnimationFrame(this.frame);
  };

  /** Grows the shared drawing buffer to hold the largest preview of this frame. */
  private fit(gl: THREE.WebGLRenderer, batch: Slot[]): void {
    let w = this.glW;
    let h = this.glH;
    for (const s of batch) {
      w = Math.max(w, s.pxW);
      h = Math.max(h, s.pxH);
    }
    if (w === this.glW && h === this.glH) return;
    this.glW = Math.ceil(w / 64) * 64;
    this.glH = Math.ceil(h / 64) * 64;
    gl.setSize(this.glW, this.glH, false);
  }

  private draw(gl: THREE.WebGLRenderer, s: Slot, now: number): void {
    const w = s.pxW;
    const h = s.pxH;
    gl.setViewport(0, 0, w, h);
    gl.setScissor(0, 0, w, h);
    gl.setScissorTest(true);
    gl.render(s.view.scene, s.view.camera);
    const ctx = s.ctx!;
    ctx.globalCompositeOperation = 'copy';
    // WebGL's origin is bottom-left: the viewport lives in the last h rows of the canvas image.
    ctx.drawImage(gl.domElement, 0, this.glH - h, w, h, 0, 0, w, h);
    s.dirty = false;
    s.lastDraw = now;
    this.stats.draws++;
  }
}

export const previewRenderer = new PreviewRenderer();

if (import.meta.env.DEV) (window as unknown as { __vmPreviews?: PreviewStats }).__vmPreviews = previewRenderer.stats;
